import { useTheme } from "./ThemeContext";
import logoLight from "../assets/quilt-logo-light.svg";
import logoDark from "../assets/quilt-logo-dark.svg";
import logoWhite from "../assets/quilt-logo-white.svg";
import logoBlack from "../assets/quilt-logo-black.svg";

type QuiltLogoProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src"> & {
  /** "color" is the full-color mark; "solid" is the single-color mark (footer, etc.). */
  variant?: "color" | "solid";
  /** Override the active site theme, e.g. for surfaces that are always dark. */
  theme?: "light" | "dark";
};

/** Quilt bird mark that picks the right asset for the current theme. */
export function QuiltLogo({
  variant = "color",
  theme: themeOverride,
  alt = "Quilt",
  className = "",
  ...props
}: QuiltLogoProps) {
  const { theme: siteTheme } = useTheme();
  const theme = themeOverride ?? siteTheme;

  const src =
    variant === "solid"
      ? theme === "dark" ? logoWhite : logoBlack
      : theme === "dark" ? logoDark : logoLight;

  return <img src={src} alt={alt} className={`object-contain ${className}`} {...props} />;
}

export default QuiltLogo;

/** Quilt mark on a rounded-square tile, styled like an app icon. */
export function QuiltAppIcon({ className = "" }: { className?: string }) {
  const { theme } = useTheme();

  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-[22%] p-1 ${
        theme === "dark"
          ? "bg-gradient-to-br from-zinc-800 to-black ring-1 ring-white/15 shadow-[0_2px_10px_rgba(139,31,166,0.35),inset_0_1px_0_rgba(255,255,255,0.12)]"
          : "bg-gradient-to-br from-white to-slate-100 ring-1 ring-black/5 shadow-[0_2px_8px_rgba(15,23,42,0.15),inset_0_1px_0_rgba(255,255,255,0.9)]"
      } ${className}`}
    >
      <QuiltLogo alt="Quilt" className="h-full w-full" />
    </span>
  );
}
